import { CoinbaseWallet } from '@web3-react/coinbase-wallet'
import { initializeConnector, Web3ReactHooks } from '@web3-react/core'
import { GnosisSafe } from '@web3-react/gnosis-safe'
import { MetaMask } from '@web3-react/metamask'
import { Network } from '@web3-react/network'
import { Connector } from '@web3-react/types'
import COINBASE_ICON from 'assets/images/coinbaseWalletIcon.svg'
import ENKRYPT_ICON from 'assets/images/enkryptWalletIcon.svg'
import GNOSIS_ICON from 'assets/images/gnosis.png'
import HANA_ICON from 'assets/images/hanaWalletIcon.svg'
import METAMASK_ICON from 'assets/images/metamask.svg'
import ONEKEY_ICON from 'assets/images/onekeyWalletIcon.svg'
import SUBWALLET_ICON from 'assets/images/subWalletIcon.svg'
import TALISMAN_ICON from 'assets/images/talismanWalletIcon.svg'
import UNIWALLET_ICON from 'assets/images/uniwallet.svg'
import WALLET_CONNECT_ICON from 'assets/images/walletConnectIcon.svg'
import INJECTED_DARK_ICON from 'assets/svg/browser-wallet-dark.svg'
import INJECTED_LIGHT_ICON from 'assets/svg/browser-wallet-light.svg'
import ARTHSWAP_LOGO from 'assets/svg/logo.svg'
import { SupportedChainId } from 'constants/chains'
import { Enkrypt } from 'lib/connectors/Enkrypt'
import { Hana } from 'lib/connectors/Hana'
import { OneKey } from 'lib/connectors/Onekey'
import { SubWallet } from 'lib/connectors/SubWallet'
import { Talisman } from 'lib/connectors/Talisman'
import { useCallback } from 'react'
import { isMobile, isNonIOSPhone } from 'utils/userAgent'

import { RPC_URLS } from '../constants/networks'
import { RPC_PROVIDERS } from '../constants/providers'
import { getIsCoinbaseWallet, getIsInjected, getIsMetaMaskWallet } from './utils'
import { UniwalletConnect, WalletConnectPopup } from './WalletConnect'

export enum ConnectionType {
  UNIWALLET = 'UNIWALLET',
  INJECTED = 'INJECTED',
  COINBASE_WALLET = 'COINBASE_WALLET',
  WALLET_CONNECT = 'WALLET_CONNECT',
  NETWORK = 'NETWORK',
  GNOSIS_SAFE = 'GNOSIS_SAFE',
  TALISMAN = 'TALISMAN',
  SUBWALLET = 'SUBWALLET',
  ONEKEY = 'ONEKEY',
  HANA = 'HANA',
  ENKRYPT = 'ENKRYPT',
}

export interface Connection {
  getName(): string
  connector: Connector
  hooks: Web3ReactHooks
  type: ConnectionType
  getIcon?(isDarkMode: boolean): string
  shouldDisplay(): boolean
  overrideActivate?: () => boolean
  isNew?: boolean
}

function onError(error: Error) {
  console.debug(`web3-react error: ${error}`)
}

const [web3Network, web3NetworkHooks] = initializeConnector<Network>(
  (actions) => new Network({ actions, urlMap: RPC_PROVIDERS, defaultChainId: 592 })
)
export const networkConnection: Connection = {
  getName: () => 'Network',
  connector: web3Network,
  hooks: web3NetworkHooks,
  type: ConnectionType.NETWORK,
  shouldDisplay: () => false,
}

const getIsCoinbaseWalletBrowser = () => isMobile && getIsCoinbaseWallet()
const getIsMetaMaskBrowser = () => isMobile && getIsMetaMaskWallet()
const getIsInjectedMobileBrowser = () => getIsCoinbaseWalletBrowser() || getIsMetaMaskBrowser()

const getShouldAdvertiseMetaMask = () =>
  !getIsMetaMaskWallet() && !isMobile && (!getIsInjected() || getIsCoinbaseWallet())
const getIsGenericInjector = () => getIsInjected() && !getIsMetaMaskWallet() && !getIsCoinbaseWallet()

const [web3Injected, web3InjectedHooks] = initializeConnector<MetaMask>((actions) => new MetaMask({ actions, onError }))

const injectedConnection: Connection = {
  // TODO(WEB-3131) re-add "Install MetaMask" string when no injector is present
  getName: () => (getIsGenericInjector() ? 'Browser Wallet' : 'MetaMask'),
  connector: web3Injected,
  hooks: web3InjectedHooks,
  type: ConnectionType.INJECTED,
  getIcon: (isDarkMode: boolean) =>
    getIsGenericInjector() ? (isDarkMode ? INJECTED_DARK_ICON : INJECTED_LIGHT_ICON) : METAMASK_ICON,
  shouldDisplay: () => getIsMetaMaskWallet() || getShouldAdvertiseMetaMask() || getIsGenericInjector(),
  // If on non-injected, non-mobile browser, prompt user to install Metamask
  overrideActivate: () => {
    if (getShouldAdvertiseMetaMask()) {
      window.open('https://metamask.io/', 'inst_metamask')
      return true
    }
    return false
  },
}
const [web3GnosisSafe, web3GnosisSafeHooks] = initializeConnector<GnosisSafe>((actions) => new GnosisSafe({ actions }))
export const gnosisSafeConnection: Connection = {
  getName: () => 'Gnosis Safe',
  connector: web3GnosisSafe,
  hooks: web3GnosisSafeHooks,
  type: ConnectionType.GNOSIS_SAFE,
  getIcon: () => GNOSIS_ICON,
  shouldDisplay: () => false,
}

const [web3WalletConnect, web3WalletConnectHooks] = initializeConnector<WalletConnectPopup>(
  (actions) => new WalletConnectPopup({ actions, onError })
)
export const walletConnectConnection: Connection = {
  getName: () => 'WalletConnect',
  connector: web3WalletConnect,
  hooks: web3WalletConnectHooks,
  type: ConnectionType.WALLET_CONNECT,
  getIcon: () => WALLET_CONNECT_ICON,
  shouldDisplay: () => !getIsInjectedMobileBrowser(),
}

const [web3UniwalletConnect, web3UniwalletConnectHooks] = initializeConnector<UniwalletConnect>(
  (actions) => new UniwalletConnect({ actions, onError })
)
export const uniwalletConnectConnection: Connection = {
  getName: () => 'Uniswap Wallet',
  connector: web3UniwalletConnect,
  hooks: web3UniwalletConnectHooks,
  type: ConnectionType.UNIWALLET,
  getIcon: () => UNIWALLET_ICON,
  shouldDisplay: () => Boolean(!getIsInjectedMobileBrowser() && !isNonIOSPhone),
  isNew: true,
}

const [web3CoinbaseWallet, web3CoinbaseWalletHooks] = initializeConnector<CoinbaseWallet>(
  (actions) =>
    new CoinbaseWallet({
      actions,
      options: {
        url: RPC_URLS[SupportedChainId.MAINNET][0],
        appName: 'Arthswap',
        appLogoUrl: ARTHSWAP_LOGO,
        reloadOnDisconnect: false,
      },
      onError,
    })
)

const coinbaseWalletConnection: Connection = {
  getName: () => 'Coinbase Wallet',
  connector: web3CoinbaseWallet,
  hooks: web3CoinbaseWalletHooks,
  type: ConnectionType.COINBASE_WALLET,
  getIcon: () => COINBASE_ICON,
  shouldDisplay: () =>
    Boolean((isMobile && !getIsInjectedMobileBrowser()) || !isMobile || getIsCoinbaseWalletBrowser()),
  // If on a mobile browser that isn't the coinbase wallet browser, deeplink to the coinbase wallet app
  overrideActivate: () => {
    if (isMobile && !getIsInjectedMobileBrowser()) {
      window.open('https://go.cb-w.com/mtUDhEZPy1', 'cbwallet')
      return true
    }
    return false
  },
}

const [web3Talisman, web3TalismanHooks] = initializeConnector<Talisman>((actions) => new Talisman({ actions, onError }))

const talismanConnection: Connection = {
  getName: () => 'Talisman',
  connector: web3Talisman,
  hooks: web3TalismanHooks,
  type: ConnectionType.TALISMAN,
  getIcon: () => TALISMAN_ICON,
  shouldDisplay: () => !!window.talismanEth,
}

const [web3SubWallet, web3SubWalletHooks] = initializeConnector<SubWallet>(
  (actions) => new SubWallet({ actions, onError })
)

const subWalletConnection: Connection = {
  getName: () => 'SubWallet',
  connector: web3SubWallet,
  hooks: web3SubWalletHooks,
  type: ConnectionType.SUBWALLET,
  getIcon: () => SUBWALLET_ICON,
  shouldDisplay: () => !!window.SubWallet,
}

const [web3OneKey, web3OneKeyHooks] = initializeConnector<OneKey>((actions) => new OneKey({ actions, onError }))

const oneKeyConnection: Connection = {
  getName: () => 'OneKey Wallet',
  connector: web3OneKey,
  hooks: web3OneKeyHooks,
  type: ConnectionType.ONEKEY,
  getIcon: () => ONEKEY_ICON,
  shouldDisplay: () => !!(window.ethereum?.isOneKey || window.$onekey),
}

const [web3Hana, web3HanaHooks] = initializeConnector<Hana>((actions) => new Hana({ actions, onError }))

const hanaConnection: Connection = {
  getName: () => 'Hana Wallet',
  connector: web3Hana,
  hooks: web3HanaHooks,
  type: ConnectionType.HANA,
  getIcon: () => HANA_ICON,
  shouldDisplay: () => !!(window.ethereum?.isHanaWallet || window.hanaWalletEth),
}

const [web3Enkrypt, web3EnkryptHooks] = initializeConnector<Enkrypt>((actions) => new Enkrypt({ actions, onError }))

const enkryptConnection: Connection = {
  getName: () => 'Enkrypt Wallet',
  connector: web3Enkrypt,
  hooks: web3EnkryptHooks,
  type: ConnectionType.ENKRYPT,
  getIcon: () => ENKRYPT_ICON,
  shouldDisplay: () => !!(window.ethereum?.isEnkrypt || window.enkrypt),
}

export function getConnections() {
  return [
    injectedConnection,
    // walletConnectConnection,
    coinbaseWalletConnection,
    gnosisSafeConnection,
    networkConnection,
    talismanConnection,
    subWalletConnection,
    oneKeyConnection,
    hanaConnection,
    enkryptConnection,
  ]
}

export function useGetConnection() {
  return useCallback((c: Connector | ConnectionType) => {
    if (c instanceof Connector) {
      const connection = getConnections().find((connection) => connection.connector === c)
      if (!connection) {
        throw Error('unsupported connector')
      }
      return connection
    } else {
      switch (c) {
        case ConnectionType.INJECTED:
          return injectedConnection
        case ConnectionType.COINBASE_WALLET:
          return coinbaseWalletConnection
        case ConnectionType.WALLET_CONNECT:
          return walletConnectConnection
        case ConnectionType.UNIWALLET:
          return uniwalletConnectConnection
        case ConnectionType.NETWORK:
          return networkConnection
        case ConnectionType.GNOSIS_SAFE:
          return gnosisSafeConnection
        case ConnectionType.TALISMAN:
          return talismanConnection
        case ConnectionType.SUBWALLET:
          return subWalletConnection
        case ConnectionType.ONEKEY:
          return oneKeyConnection
        case ConnectionType.HANA:
          return hanaConnection
        case ConnectionType.ENKRYPT:
          return enkryptConnection
      }
    }
  }, [])
}
